import React, { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Box, Divider, Typography, Button, LinearProgress, CircularProgress, TextField, Tooltip } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import { useGetOrgByIdQuery } from "state/rtkQuery/org/orgQueries";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import {
  useGetCopilotUsageQuery,
  useGetCopilotUsageMonthlyQuery,
  useGetCopilotInfoQuery,
  useGetCopilotUsageByCostCenterQuery,
  useStartCopilotExportJobMutation,
  useStartCopilotBudgetExportJobMutation,
} from "state/rtkQuery/copilot/copilotQueries";

import StatCards from "./components/CopilotDashboard/StatCards";
import PoolPieChart from "./components/CopilotDashboard/PoolPieChart";
import EmptyState from "./components/CopilotDashboard/EmptyState";
import EngineerTable from "./components/CopilotDashboard/EngineerTable";
import ModelTable from "./components/CopilotDashboard/ModelTable";
import CostCenterTable from "./components/CopilotDashboard/CostCenterTable";
import CostCenterSelector from "./components/CopilotDashboard/CostCenterSelector";
import UsageCharts from "./components/CopilotDashboard/UsageCharts";
import { useDashboardSettings } from "./components/CopilotDashboard/useDashboardSettings";
import { useCopilotExportJob } from "./components/CopilotDashboard/useCopilotExportJob";
import CopilotRefreshButton from "./components/CopilotDashboard/CopilotRefreshButton";
import SeatBreakdownChip from "./components/CopilotDashboard/SeatBreakdownChip";
import { useCopilotActivityCapabilities, useCopilotExportCapabilities } from "components/RequirePermission/useCapabilities";
import { PRESETS, DEFAULT_PRESET_KEY, getPresetRange } from "./components/CopilotDashboard/datePresets";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

import "./CopilotDashboard.css";

const ACTION_GREEN = "#26890d";
const ACTION_GREEN_HOVER = "#26890d";
const ACTION_BLUE = "#006eff";

const pickerTheme = createTheme({
  palette: { primary: { main: "#26890d" } },
});

// Resolves initial date-range state from the URL's ?preset= (and ?from=/?to=
// for preset=custom) so a shared/bookmarked link restores the same view.
// Falls back to the default preset when the param is missing or unrecognized —
// existing links with no ?preset= at all behave exactly as before.
const resolveInitialRange = (searchParams) => {
  const presetKey = searchParams.get("preset") ?? DEFAULT_PRESET_KEY;

  if (presetKey === "custom") {
    const from = dayjs(searchParams.get("from"));
    const to = dayjs(searchParams.get("to"));
    if (from.isValid() && to.isValid()) {
      return { presetKey, from, to, isCustom: true };
    }
    // Malformed/missing custom dates in the URL — fall back to default rather
    // than rendering with invalid dates.
    const fallback = getPresetRange(DEFAULT_PRESET_KEY);
    return { presetKey: DEFAULT_PRESET_KEY, ...fallback, isCustom: false };
  }

  const range = getPresetRange(presetKey);
  if (!range) {
    const fallback = getPresetRange(DEFAULT_PRESET_KEY);
    return { presetKey: DEFAULT_PRESET_KEY, ...fallback, isCustom: false };
  }
  return { presetKey, ...range, isCustom: false };
};

const CopilotDashboard = () => {
  const { orgId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialRange = resolveInitialRange(searchParams);

  // UI picker state
  const [fromDate, setFromDate] = useState(initialRange.from);
  const [toDate, setToDate] = useState(initialRange.to);
  const [activePreset, setActivePreset] = useState(initialRange.presetKey);
  const [showCustom, setShowCustom] = useState(initialRange.isCustom);

  // Applied dates that drive the API query (updated immediately on preset, on Apply for custom)
  const [queryFrom, setQueryFrom] = useState(initialRange.from.format("YYYY-MM-DD"));
  const [queryTo, setQueryTo] = useState(initialRange.to.format("YYYY-MM-DD"));

  // Cost center scoping — mirrored into the URL so a selected cost center
  // (e.g. from clicking a bar in the cost-center chart) survives a refresh or
  // is shareable via link. null/"" means "no cost center selected" — show the
  // org-wide view, which is the default.
  const [costCenterId, setCostCenterId] = useState(searchParams.get("cost_center_id") ?? null);

  const handleCostCenterChange = (nextCostCenterId) => {
    setCostCenterId(nextCostCenterId);
    const next = new URLSearchParams(searchParams);
    if (nextCostCenterId) next.set("cost_center_id", nextCostCenterId);
    else next.delete("cost_center_id");
    setSearchParams(next, { replace: true });
  };

  // Org and platform data
  const { data: orgData } = useGetOrgByIdQuery(orgId);
  const { data: platformData } = useGetPlatformsQuery();
  const platformId = platformData?._id;

  // Global dashboard settings (visible sections + Deloitte overage rate) — one
  // shared config for everyone. Read-only here; editable only from the
  // ecr-ops enterprise Copilot dashboard (see DashboardSettingsMenu.jsx).
  const { visibleSections, deloitteOverageRate } = useDashboardSettings();

  // Export handlers — async job flow: kick off a job, poll its status until
  // it completes (surfacing progress in a toast that stays up for the whole
  // operation so the user knows not to navigate away), then fetch a
  // short-lived signed download URL and open it in a new tab. Scoped to the
  // selected cost center when one is active, otherwise the org. Shared with
  // the enterprise dashboard via useCopilotExportJob — see that hook for the
  // poll/download/persistence/resume logic; "admin" namespaces this
  // dashboard's persisted jobs so they never collide with enterprise's.
  const [startExportJob] = useStartCopilotExportJobMutation();
  const [startBudgetExportJob] = useStartCopilotBudgetExportJobMutation();
  const { isExporting, isExportingBudget, makeHandlers } = useCopilotExportJob("admin");
  const { canViewExport, canCreateUsageExport, canCreateBudgetExport } = useCopilotExportCapabilities(orgId);
  const { canViewActivity } = useCopilotActivityCapabilities(orgId);
  const navigate = useNavigate();

  const isCostCenterScoped = Boolean(costCenterId);

  // Org-level usage detail + summary query — skipped once a cost center is
  // selected, since none of its data (summary, records) is read in that state;
  // the selected cost center's own summary comes from selectedCostCenterData below.
  const {
    data: usageData,
    currentData: usageCurrentData,
    isLoading: isUsageLoading,
    isFetching: isUsageFetching,
    error: usageError,
  } = useGetCopilotUsageQuery(
    { orgId, startDate: queryFrom, endDate: queryTo },
    { skip: isCostCenterScoped }
  );

  // Monthly chart query — org-wide only; no cost-center-scoped equivalent
  // exists, and the two monthly trend charts are hidden entirely once a cost
  // center is selected, so this is skipped in that state too.
  const {
    data: monthlyApiData,
    isLoading: isMonthlyLoading,
  } = useGetCopilotUsageMonthlyQuery({ orgId }, { skip: isCostCenterScoped });

  // Org-level plan/seat info (plan badge in header) — always fetched; the
  // plan badge is shown regardless of cost-center selection.
  const { data: copilotInfo } = useGetCopilotInfoQuery({ orgId });
  const planType = copilotInfo?.plan_type ?? null;
  const planBadge =
    planType === "enterprise"
      ? { label: "Copilot Enterprise", color: "#7B5EA7", bg: "rgba(123,94,167,0.08)" }
      : planType === "business"
        ? { label: "Copilot Business", color: "#006eff", bg: "rgba(0,110,255,0.08)" }
        : { label: "Copilot plan unknown", color: "#888", bg: "rgba(136,136,136,0.08)" };

  // Dropdown list — always org-scoped (org_id only, never cost_center_id), so
  // the selector's options always reflect this org's cost centers regardless
  // of what's currently selected. This is what populates the <Select>.
  const { data: costCenterListData, currentData: costCenterListCurrentData, isLoading: isCostCenterListLoading, isFetching: isCostCenterListFetching } =
    useGetCopilotUsageByCostCenterQuery(
      { orgId, platformId, startDate: queryFrom, endDate: queryTo },
      { skip: !orgId || !platformId }
    );
  const costCenterGroups = costCenterListData?.groups ?? [];

  // Selected cost center's own summary — fetched with cost_center_id only
  // (org_id omitted), so once a cost center is picked the numbers reflect
  // that cost center's true total across every org, not just this one.
  const { data: selectedCostCenterData, currentData: selectedCostCenterCurrentData, isLoading: isSelectedCostCenterLoading, isFetching: isSelectedCostCenterFetching } =
    useGetCopilotUsageByCostCenterQuery(
      { costCenterId, platformId, startDate: queryFrom, endDate: queryTo },
      { skip: !isCostCenterScoped || !platformId }
    );
  const selectedCostCenterGroup = selectedCostCenterData?.groups?.[0] ?? null;

  // Human-readable scope name — the selected cost center's name once picked,
  // otherwise the org's name. Used for export filenames and button tooltips
  // so it's always clear what an export/action is scoped to.
  const exportScopeName = isCostCenterScoped
    ? selectedCostCenterGroup?.cost_center_name ?? costCenterId
    : orgData?.name ?? orgId;

  const { handleExport, handleBudgetExport } = makeHandlers({
    platformId,
    exportScopeName,
    queryFrom,
    queryTo,
    startUsageJob: () => startExportJob({ orgId, costCenterId, platformId, fromDate: queryFrom, toDate: queryTo }).unwrap(),
    startBudgetJob: () => startBudgetExportJob({ orgId, costCenterId, platformId, fromDate: queryFrom, toDate: queryTo }).unwrap(),
  });

  // Full-page loading only for a first load or a changed date range/scope
  // (no result for the current arguments yet). A refetch of arguments that
  // already have data — the Refresh button or the timed auto-refresh — runs
  // silently in the background while the existing numbers stay on screen.
  const isCostCenterLoading = isCostCenterListLoading || (isCostCenterListFetching && !costCenterListCurrentData);
  const isCostCenterFetching =
    isSelectedCostCenterLoading || (isSelectedCostCenterFetching && !selectedCostCenterCurrentData);

  const isLoading =
    isUsageLoading ||
    (isUsageFetching && !usageCurrentData) ||
    isMonthlyLoading ||
    (isCostCenterScoped && isCostCenterFetching);

  // Derive component-ready data from API responses — sourced from the org-wide
  // summary by default, or from the selected cost center's own summary once
  // one is picked in the selector.
  const summary = isCostCenterScoped ? selectedCostCenterGroup?.summary ?? null : usageData?.summary ?? null;
  const hasData = isCostCenterScoped
    ? !isLoading && Boolean(selectedCostCenterGroup)
    : !isLoading && Array.isArray(usageData?.records) && usageData.records.length > 0;

  const statsSummaryData = summary
    ? {
      totalAICConsumed: summary.total_gross_credits ?? 0,
      totalSpend: (summary.total_gross_credits ?? 0) * 0.01,
      totalPoolSavings: summary.total_pool_savings_amount ?? 0,
      totalOverageBilled: summary.total_overage_amount ?? 0,
      // Deloitte's negotiated overage rate vs the $0.01/AIC list price the
      // overage amount is billed at — configurable via dashboard settings.
      deloitteCost: (summary.total_overage_credits ?? 0) * deloitteOverageRate,
    }
    : null;

  const poolChartData = summary
    ? {
      total_pool_covered_credits: summary.total_pool_covered_credits ?? 0,
      total_overage_credits: summary.total_overage_credits ?? 0,
      pool_utilization_pct: summary.pool_utilization_pct ?? 0,
      total_pool_savings_amount: summary.total_pool_savings_amount ?? 0,
      total_overage_amount: summary.total_overage_amount ?? 0,
    }
    : null;

  const modelData = summary?.usage_stats ?? [];
  const monthlyChartData = monthlyApiData?.monthly ?? [];
  const dailyRecords = usageData?.records ?? [];

  // Apply button (custom range only) — commits picker values to query args
  // and mirrors them into the URL so a custom range is also shareable/bookmarkable.
  const handleApplyDateFilter = () => {
    const from = fromDate.format("YYYY-MM-DD");
    const to = toDate.format("YYYY-MM-DD");
    setQueryFrom(from);
    setQueryTo(to);
    setSearchParams({ preset: "custom", from, to }, { replace: true });
  };

  const handlePresetClick = (preset) => {
    setActivePreset(preset.key);
    if (preset.key === "custom") {
      setShowCustom(true);
      // Don't touch the URL yet — Custom has no range until Apply is clicked.
      return;
    }
    setShowCustom(false);
    const { from, to } = getPresetRange(preset.key);
    setFromDate(from);
    setToDate(to);
    setQueryFrom(from.format("YYYY-MM-DD"));
    setQueryTo(to.format("YYYY-MM-DD"));
    // replace (not push) — a date-range change is filter state, not a new page
    // to step back through via the browser's back button.
    setSearchParams({ preset: preset.key }, { replace: true });
  };

  return (
    <Box
      sx={{
        flex: "1",
        marginTop: { xs: "1.5rem", md: "4rem" },
        marginBottom: { xs: "1.5rem", md: "4rem" },
        marginLeft: { xs: "1rem", md: "4rem" },
        marginRight: { xs: "1rem", md: "4rem" },
        backgroundColor: "#F3F3F3",
        borderRadius: "12px",
        padding: { xs: "16px", md: "24px" },
      }}
    >
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          alignItems: { xs: "flex-start", lg: "center" },
          justifyContent: "space-between",
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <svg role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="#1a1a1a"><path d="M23.922 16.997C23.061 18.492 18.063 22.02 12 22.02 5.937 22.02.939 18.492.078 16.997A.641.641 0 0 1 0 16.741v-2.869a.883.883 0 0 1 .053-.22c.372-.935 1.347-2.292 2.605-2.656.167-.429.414-1.055.644-1.517a10.098 10.098 0 0 1-.052-1.086c0-1.331.282-2.499 1.132-3.368.397-.406.89-.717 1.474-.952C7.255 2.937 9.248 1.98 11.978 1.98c2.731 0 4.767.957 6.166 2.093.584.235 1.077.546 1.474.952.85.869 1.132 2.037 1.132 3.368 0 .368-.014.733-.052 1.086.23.462.477 1.088.644 1.517 1.258.364 2.233 1.721 2.605 2.656a.841.841 0 0 1 .053.22v2.869a.641.641 0 0 1-.078.256Zm-11.75-5.992h-.344a4.359 4.359 0 0 1-.355.508c-.77.947-1.918 1.492-3.508 1.492-1.725 0-2.989-.359-3.782-1.259a2.137 2.137 0 0 1-.085-.104L4 11.746v6.585c1.435.779 4.514 2.179 8 2.179 3.486 0 6.565-1.4 8-2.179v-6.585l-.098-.104s-.033.045-.085.104c-.793.9-2.057 1.259-3.782 1.259-1.59 0-2.738-.545-3.508-1.492a4.359 4.359 0 0 1-.355-.508Zm2.328 3.25c.549 0 1 .451 1 1v2c0 .549-.451 1-1 1-.549 0-1-.451-1-1v-2c0-.549.451-1 1-1Zm-5 0c.549 0 1 .451 1 1v2c0 .549-.451 1-1 1-.549 0-1-.451-1-1v-2c0-.549.451-1 1-1Zm3.313-6.185c.136 1.057.403 1.913.878 2.497.442.544 1.134.938 2.344.938 1.573 0 2.292-.337 2.657-.751.384-.435.558-1.15.558-2.361 0-1.14-.243-1.847-.705-2.319-.477-.488-1.319-.862-2.824-1.025-1.487-.161-2.192.138-2.533.529-.269.307-.437.808-.438 1.578v.021c0 .265.021.562.063.893Zm-1.626 0c.042-.331.063-.628.063-.894v-.02c-.001-.77-.169-1.271-.438-1.578-.341-.391-1.046-.69-2.533-.529-1.505.163-2.347.537-2.824 1.025-.462.472-.705 1.179-.705 2.319 0 1.211.175 1.926.558 2.361.365.414 1.084.751 2.657.751 1.21 0 1.902-.394 2.344-.938.475-.584.742-1.44.878-2.497Z" /></svg>
          <Box>
            {orgData?.name && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Typography
                  sx={{
                    fontSize: "0.95rem",
                    fontWeight: 700,
                    color: "#43B02A",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  {orgData.name}
                </Typography>
                {planType !== null && (
                  <Box
                    sx={{
                      px: 1.25,
                      py: 0.3,
                      borderRadius: "20px",
                      backgroundColor: planBadge.bg,
                      border: `1px solid ${planBadge.color}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        color: planBadge.color,
                        letterSpacing: "0.02em",
                        textTransform: "none",
                      }}
                    >
                      {planBadge.label}
                    </Typography>
                  </Box>
                )}
                <SeatBreakdownChip
                  seatBreakdown={copilotInfo?.seat_breakdown}
                  onViewUsers={canViewActivity ? () => navigate(`/admin/${orgId}/copilot-users`) : undefined}
                />
              </Box>
            )}
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "#1a1a1a", lineHeight: 1.2 }}
            >
              Copilot Dashboard
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "#888", mt: 0.3 }}>
              AIC usage &amp; spend analytics
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.6,
                mt: 1,
                px: 1.2,
                py: 0.35,
                backgroundColor: "rgba(38,137,13,0.07)",
                border: "1px solid rgba(38,137,13,0.22)",
                borderRadius: "20px",
                width: "fit-content",
              }}
            >
              <CalendarTodayIcon sx={{ fontSize: "12px", color: "#26890d" }} />
              <Typography sx={{ fontSize: "12px", color: "#26890d", fontWeight: 700, letterSpacing: "0.01em" }}>
                {queryFrom} &rarr; {queryTo}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Right side: Export buttons + date controls */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "flex-start", lg: "flex-end" },
            gap: 1,
            width: { xs: "100%", lg: "auto" },
          }}
        >
          {/* Export buttons. Dashboard settings (visible sections, Deloitte
              overage rate) are edited only from the ecr-ops enterprise
              Copilot dashboard — see DashboardSettingsMenu.jsx — since they
              apply to every org's dashboard, not just this one.
              canViewExport hides both buttons entirely; canCreateUsageExport/
              canCreateBudgetExport disable each individually (with its own
              tooltip) once visible. */}
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <CopilotRefreshButton />
            {canViewExport && (<>
            <Tooltip
              title={
                !canCreateUsageExport
                  ? "You don't have permission to export usage data"
                  : !hasData
                    ? "No data to export for this range"
                    : `Exports usage data for ${exportScopeName}`
              }
              placement="top"
              arrow
            >
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={isExporting ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <FileDownloadOutlinedIcon />}
                  onClick={handleExport}
                  disabled={isExporting || !platformId || !hasData || !canCreateUsageExport}
                  sx={{
                    borderRadius: "16px",
                    textTransform: "none",
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    borderColor: "#ccc",
                    color: "#555",
                    px: 1.5,
                    py: 0.3,
                    minWidth: 0,
                    "&:hover": { borderColor: ACTION_GREEN, color: ACTION_GREEN },
                    "&.Mui-disabled": {
                      borderRadius: "16px",
                      textTransform: "none",
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      borderColor: "#e0e0e0",
                      color: "#bbb",
                    },
                  }}
                >
                  {isExporting ? "Exporting..." : "Export Usage Data"}
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={
                !canCreateBudgetExport
                  ? "You don't have permission to export budget data"
                  : !hasData
                    ? "No data to export for this range"
                    : `Exports budget data for ${exportScopeName}`
              }
              placement="top"
              arrow
            >
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={isExportingBudget ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <FileDownloadOutlinedIcon />}
                  onClick={handleBudgetExport}
                  disabled={isExportingBudget || !platformId || !hasData || !canCreateBudgetExport}
                  sx={{
                    borderRadius: "16px",
                    textTransform: "none",
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    borderColor: "#ccc",
                    color: "#555",
                    px: 1.5,
                    py: 0.3,
                    minWidth: 0,
                    "&:hover": { borderColor: ACTION_GREEN, color: ACTION_GREEN },
                    "&.Mui-disabled": {
                      borderRadius: "16px",
                      textTransform: "none",
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      borderColor: "#e0e0e0",
                      color: "#bbb",
                    },
                  }}
                >
                  {isExportingBudget ? "Exporting..." : "Export Budget"}
                </Button>
              </span>
            </Tooltip>
            </>)}
          </Box>
          {/* Preset chips */}
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {PRESETS.map((p) => (
              <Button
                key={p.key}
                size="small"
                onClick={() => handlePresetClick(p)}
                sx={{
                  borderRadius: "16px",
                  textTransform: "none",
                  fontSize: "0.8rem",
                  fontWeight: activePreset === p.key ? 600 : 400,
                  backgroundColor: activePreset === p.key ? ACTION_GREEN : "transparent",
                  color: activePreset === p.key ? "#fff" : "#555",
                  border: `1px solid ${activePreset === p.key ? ACTION_GREEN : "#ccc"}`,
                  px: 1.5,
                  py: 0.3,
                  minWidth: 0,
                  "&:hover": {
                    backgroundColor: activePreset === p.key ? ACTION_GREEN_HOVER : "#f0f0f0",
                    border: `1px solid ${activePreset === p.key ? ACTION_GREEN_HOVER : "#bbb"}`,
                  },
                }}
              >
                {p.label}
              </Button>
            ))}
          </Box>

          {/* Custom date range - shown only when Custom chip is active */}
          {showCustom && (
            <ThemeProvider theme={pickerTheme}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                  <DatePicker
                    label="From"
                    value={fromDate}
                    onChange={(val) => setFromDate(val)}
                    maxDate={toDate ?? dayjs()}
                    renderInput={(params) => (
                      <TextField {...params} size="small" sx={{ backgroundColor: "#fff", borderRadius: 1, width: 150 }} />
                    )}
                  />
                  <DatePicker
                    label="To"
                    value={toDate}
                    onChange={(val) => setToDate(val)}
                    minDate={fromDate}
                    maxDate={dayjs()}
                    renderInput={(params) => (
                      <TextField {...params} size="small" sx={{ backgroundColor: "#fff", borderRadius: 1, width: 150 }} />
                    )}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SearchIcon />}
                    onClick={handleApplyDateFilter}
                    disabled={isLoading}
                    sx={{
                      backgroundColor: ACTION_GREEN,
                      color: "#fff",
                      fontWeight: 400,
                      textTransform: "none",
                      fontSize: "0.95rem",
                      height: 35,
                      minHeight: 35,
                      padding: "0 10px",
                      lineHeight: 1,
                      "& .MuiButton-startIcon": { marginRight: "4px" },
                      "&:hover": { backgroundColor: ACTION_GREEN_HOVER },
                      "&:disabled": { backgroundColor: "#ccc", color: "#999" },
                    }}
                  >
                    {isLoading ? "Fetching..." : "Apply"}
                  </Button>
                </Box>
              </LocalizationProvider>
            </ThemeProvider>
          )}
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Cost Center scoping is disabled for now on the org-admin dashboard —
          org owners always see organization-wide data. */}

      {/* Loading Progress Bar */}
      {isLoading && <LinearProgress sx={{ mb: 2, backgroundColor: "#f0f0f0", "& .MuiLinearProgress-bar": { backgroundColor: ACTION_GREEN } }} />}

      {/* Show Loading, Empty State, Error, or Dashboard */}
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
            backgroundColor: "#fafafa",
            borderRadius: "12px",
            border: "1px solid #e8e8e8",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <CircularProgress sx={{ color: ACTION_BLUE }} />
            <Typography sx={{ fontSize: "0.9rem", color: "#888" }}>Fetching data...</Typography>
          </Box>
        </Box>
      )}

      {!isLoading && usageError && !isCostCenterScoped && (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography sx={{ color: "#E53935", fontWeight: 600 }}>
            {usageError?.data?.detail ?? "Failed to load usage data. Please try again."}
          </Typography>
        </Box>
      )}

      {!isLoading && !usageError && !hasData && !isCostCenterScoped && (
        <EmptyState fromDate={queryFrom} toDate={queryTo} onPresetClick={handlePresetClick} />
      )}

      {!isLoading && isCostCenterScoped && !hasData && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
            backgroundColor: "#fafafa",
            borderRadius: "12px",
            border: "1px solid #e8e8e8",
          }}
        >
          <Typography sx={{ fontSize: "0.9rem", color: "#999" }}>
            No usage data for this cost center in the selected date range.
          </Typography>
        </Box>
      )}

      {!isLoading && hasData && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Section 1 - Stat Cards */}
          {visibleSections.stat_cards && statsSummaryData && (
            <StatCards
              data={statsSummaryData}
              deloitteOverageRate={deloitteOverageRate}
              orgId={orgId}
              costCenterId={costCenterId}
              platformId={platformId}
            />
          )}

          {/* Section 2 - Pie Chart + Model Table side by side */}
          {(visibleSections.pool_chart || visibleSections.model_table) && (
            <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, alignItems: "stretch" }}>
              {visibleSections.pool_chart && (
                <Box sx={{ flex: { xs: "1 1 auto", md: "0 0 280px" } }}>
                  {poolChartData && <PoolPieChart data={poolChartData} />}
                </Box>
              )}
              {visibleSections.model_table && (
                <Box sx={{ flex: "1", minWidth: 0, border: "1px solid #e8e8e8", borderRadius: "12px", overflow: "hidden", backgroundColor: "#fff" }}>
                  <ModelTable
                    models={modelData}
                    totalOverageCredits={summary?.total_overage_credits ?? 0}
                    totalOverageAmount={summary?.total_overage_amount ?? 0}
                    deloitteOverageRate={deloitteOverageRate}
                    orgId={orgId}
                    costCenterId={costCenterId}
                    platformId={platformId}
                    fromDate={queryFrom}
                    toDate={queryTo}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Section 3 - Engineer Table */}
          {visibleSections.engineer_table && (
            <Box sx={{ border: "1px solid #e8e8e8", borderRadius: "12px", overflow: "hidden", backgroundColor: "#fff" }}>
              <EngineerTable
                orgId={orgId}
                costCenterId={costCenterId}
                platformId={platformId}
                fromDate={queryFrom}
                toDate={queryTo}
                deloitteOverageRate={deloitteOverageRate}
              />
            </Box>
          )}

          {/* Section 3b - Cost Center Table — org-wide only. Cost-center
              scoping (via onSelectCostCenter) is disabled for now on the
              org-admin dashboard, so this chart is informational only. */}
          {!isCostCenterScoped && visibleSections.cost_center_chart && (
            <CostCenterTable
              orgId={orgId}
              platformId={platformId}
              fromDate={queryFrom}
              toDate={queryTo}
              orgName={orgData?.name}
              deloitteOverageRate={deloitteOverageRate}
            />
          )}

          {/* Section 4 - Monthly Charts — org-wide only; no cost-center-scoped
              monthly trend data exists yet. */}
          {!isCostCenterScoped && (
            <UsageCharts
              data={monthlyChartData}
              dailyRecords={dailyRecords}
              fromDate={queryFrom}
              toDate={queryTo}
              showMonthlyTrend={visibleSections.monthly_trend_chart}
              showModelUsageTrend={visibleSections.model_usage_trend_chart}
              showPoolCoverageTrend={visibleSections.pool_coverage_trend_chart}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default CopilotDashboard;
